import React, { useState, useRef } from 'react';
import { X, Upload, Loader, Trash2 } from 'lucide-react';
import { uploadFile, validateFile, formatFileSize } from '../../services/storageService';
import '../admin/StorageUpload.css';

const ClientStorageUpload = ({ currentUser, activeProject, milestones, onClose, onSuccess }) => {
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const uploadingRef = useRef(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e) => {
    if (e.target.files?.length) addFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const addFiles = (newFiles) => {
    const valid = [];
    for (const f of newFiles) {
      const validation = validateFile(f);
      if (!validation.valid) { alert(`${f.name}: ${validation.error}`); continue; }
      valid.push(f);
    }
    setFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

  const handleUpload = async () => {
    if (uploadingRef.current) return;
    if (files.length === 0) { alert('Select at least one file'); return; }
    uploadingRef.current = true;

    const milestone = milestones.find(m => m.id === selectedMilestone);

    setUploading(true);
    setUploadStatus(files.map(f => ({ name: f.name, progress: 0, done: false, error: null })));

    let allSuccess = true;

    for (let i = 0; i < files.length; i++) {
      const metadata = {
        clientId: currentUser.uid,
        clientName: currentUser.displayName || currentUser.email,
        projectId: activeProject?.id || null,
        projectName: activeProject?.name || null,
        projectStatus: activeProject?.status || null,
        category: 'client',
        uploadedBy: currentUser.uid,
        uploadedByName: currentUser.displayName || currentUser.email,
        milestoneId: milestone?.id || null,
        milestoneName: milestone?.title || null,
      };

      try {
        const result = await uploadFile(files[i], metadata, (progress) => {
          setUploadStatus(prev => prev.map((s, idx) => idx === i ? { ...s, progress } : s));
        });

        if (result.success) {
          setUploadStatus(prev => prev.map((s, idx) => idx === i ? { ...s, progress: 100, done: true } : s));
        } else {
          setUploadStatus(prev => prev.map((s, idx) => idx === i ? { ...s, error: result.error } : s));
          allSuccess = false;
        }
      } catch (error) {
        setUploadStatus(prev => prev.map((s, idx) => idx === i ? { ...s, error: error.message } : s));
        allSuccess = false;
      }
    }

    uploadingRef.current = false;
    if (allSuccess) onSuccess();
    else setUploading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content storage-upload-modal">
        <div className="modal-header">
          <h2>Upload Files</h2>
          <button onClick={onClose} className="btn-close" disabled={uploading}><X size={20} /></button>
        </div>

        <div className="upload-form">
          <div className="form-group">
            <label>Project</label>
            <select disabled>
              <option>{activeProject ? activeProject.name : 'No active project (General)'}</option>
            </select>
          </div>

          {activeProject && milestones.length > 0 && (
            <div className="form-group">
              <label>
                Milestone
                <span className="form-optional" style={{ color: '#9e9e9e', fontWeight: 400, fontSize: 12, marginLeft: 6 }}>(optional)</span>
              </label>
              <select value={selectedMilestone} onChange={(e) => setSelectedMilestone(e.target.value)} disabled={uploading}>
                <option value="">No milestone</option>
                {milestones.map(m => (
                  <option key={m.id} value={m.id}>
                    {m.title}{m.urgent ? ' 🔥' : ''}{m.status === 'completed' ? ' ✅' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Files <span className="required">*</span></label>
            <div
              className={`upload-area ${dragActive ? 'drag-active' : ''} ${files.length > 0 ? 'has-file' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload size={40} />
              <p>Drag & drop files here</p>
              <p className="upload-hint">or</p>
              <label className="btn-browse">
                Browse Files
                <input type="file" onChange={handleFileInput} disabled={uploading} multiple hidden />
              </label>
              <p className="upload-limits">Max 50MB per file</p>
            </div>
          </div>

          {files.length > 0 && (
            <div className="upload-file-list">
              {files.map((f, i) => {
                const status = uploadStatus[i];
                return (
                  <div key={i} className="upload-file-item">
                    <div className="upload-file-info">
                      <span className="upload-file-name">{f.name}</span>
                      <span className="upload-file-size">{formatFileSize(f.size)}</span>
                    </div>
                    {status ? (
                      <div className="upload-file-progress">
                        {status.error ? (
                          <span className="upload-file-error">✕ {status.error}</span>
                        ) : status.done ? (
                          <span className="upload-file-done">✓ Done</span>
                        ) : (
                          <>
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${status.progress}%` }} />
                            </div>
                            <span className="progress-text">{Math.round(status.progress)}%</span>
                          </>
                        )}
                      </div>
                    ) : (
                      !uploading && (
                        <button onClick={() => removeFile(i)} className="btn-remove-file-item" title="Remove">
                          <Trash2 size={14} />
                        </button>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {uploading && uploadStatus.length > 0 && (
            <p className="upload-overall-status">
              {uploadStatus.filter(s => s.done).length} / {files.length} uploaded
            </p>
          )}

          <div className="form-actions">
            <button onClick={onClose} className="btn-cancel" disabled={uploading}>Cancel</button>
            <button
              onClick={handleUpload}
              className="btn-upload-submit"
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <><Loader size={16} className="spinner" />Uploading...</>
              ) : (
                <><Upload size={16} />Upload {files.length > 1 ? `${files.length} Files` : 'File'}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientStorageUpload;
